import type { ReactNode } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { DURATION, EASE, STAGGER, TRAVEL } from './tokens'

/**
 * `<Stagger>` — wraps a parent so its `<StaggerItem>` children fade-up in
 * a cascade. Use for a row of tiles, a list of cart lines, a benefit
 * grid — anywhere a series of equivalent items should feel rhythmic.
 *
 * The parent fires when scrolled into view (`viewport.once`). Reduced
 * motion collapses the cascade to a plain opacity fade.
 */
export function Stagger({
  children,
  step = STAGGER.standard,
  delayChildren = 0,
  amount = 0.1,
  className,
  as = 'div'
}: {
  readonly children: ReactNode
  readonly step?: number
  readonly delayChildren?: number
  readonly amount?: number
  readonly className?: string
  readonly as?: 'div' | 'section' | 'ul' | 'ol'
}): JSX.Element {
  const Tag = motion[as]
  return (
    <Tag
      className={className}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount }}
      variants={{
        hidden: {},
        show: { transition: { staggerChildren: step, delayChildren } }
      }}
    >
      {children}
    </Tag>
  )
}

/**
 * Sibling of `<Stagger>`. Each item fades up under the parent's cascade.
 * Travel distance is the per-item delta; the timing is owned by the
 * parent.
 */
export function StaggerItem({
  children,
  travel = TRAVEL.tight,
  duration = DURATION.standard,
  className,
  as = 'div'
}: {
  readonly children: ReactNode
  readonly travel?: number
  readonly duration?: number
  readonly className?: string
  readonly as?: 'div' | 'li' | 'article' | 'section'
}): JSX.Element {
  const reduce = useReducedMotion() === true
  const Tag = motion[as]
  return (
    <Tag
      className={className}
      variants={{
        hidden: { opacity: 0, y: reduce ? 0 : travel },
        show: { opacity: 1, y: 0, transition: { duration: reduce ? 0.2 : duration, ease: EASE } }
      }}
    >
      {children}
    </Tag>
  )
}
